import { useState, useCallback, useMemo } from 'react';
import { useVIPUsers, useVIPTransactions, adminApi } from '@hooks/useAdminApi';
import { toastManager } from './ToastContainer';

function VIPManager() {
    const [grantEmail, setGrantEmail] = useState('');
    const [grantPackage, setGrantPackage] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [transactionSearch, setTransactionSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const { users, isLoading: usersLoading, mutate: mutateUsers } = useVIPUsers(userSearch, statusFilter);
    const { transactions, isLoading: transactionsLoading, mutate: mutateTransactions } = useVIPTransactions(transactionSearch, statusFilter);
    
    const handleGrantVIP = useCallback(async (e) => {
        e.preventDefault();
        
        if (!grantEmail || !grantPackage) {
            toastManager.error('Error', 'Please fill all fields');
            return;
        }
        
        if (isSubmitting) return;
        
        try {
            setIsSubmitting(true);
            const result = await adminApi.grantVIP(grantEmail, grantPackage);
            if (result.success) {
                toastManager.success('Success', `VIP granted to ${grantEmail}`);
                setGrantEmail('');
                setGrantPackage('');
                mutateUsers();
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toastManager.error('Error', error.message || 'Failed to grant VIP');
        } finally {
            setIsSubmitting(false);
        }
    }, [grantEmail, grantPackage, isSubmitting, mutateUsers]);
    
    const formatDate = useCallback((dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }, []);
    
    return (
        <div className="admin-section">
            <h2 className="admin-section-title">
                <i className="fas fa-crown"></i> VIP Manager
            </h2>
            
            {/* Grant VIP Form */}
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '30px', marginBottom: '30px' }}>
                <h3 style={{ color: '#d4af37', marginBottom: '20px' }}>
                    <i className="fas fa-user-plus"></i> Grant VIP to User
                </h3>
                <form onSubmit={handleGrantVIP}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                        <div className="admin-form-group">
                            <label>User Email</label>
                            <input
                                type="email"
                                className="admin-input"
                                value={grantEmail}
                                onChange={(e) => setGrantEmail(e.target.value)}
                                placeholder="user@email.com"
                                required
                            />
                        </div>
                        <div className="admin-form-group">
                            <label>VIP Package</label>
                            <select 
                                className="admin-select"
                                value={grantPackage}
                                onChange={(e) => setGrantPackage(e.target.value)}
                                required
                            >
                                <option value="">Select Package</option>
                                <option value="1day">1 Day - Rp 2,000</option>
                                <option value="3days">3 Days - Rp 5,000</option>
                                <option value="7days">7 Days - Rp 8,000</option>
                                <option value="14days">14 Days - Rp 14,000</option>
                                <option value="1month">1 Month - Rp 20,000</option>
                                <option value="6months">6 Months - Rp 50,000</option>
                                <option value="1year">1 Year - Rp 80,000</option>
                                <option value="permanent">Permanent - Rp 120,000</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <button type="submit" className="admin-btn" style={{ width: '100%' }} disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Granting...</>
                                ) : (
                                    <><i className="fas fa-crown"></i> Grant VIP</>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            
            {/* VIP Users List */}
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '30px', marginBottom: '30px' }}>
                <h3 style={{ color: '#d4af37', marginBottom: '20px' }}>
                    <i className="fas fa-users"></i> VIP Users
                </h3>
                
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        className="admin-input"
                        placeholder="Search VIP users..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        style={{ flex: 1, minWidth: '250px' }}
                    />
                    <button className="admin-btn admin-btn-secondary" onClick={() => mutateUsers()}>
                        <i className="fas fa-sync-alt"></i> Refresh
                    </button>
                </div>
                
                {usersLoading ? (
                    <div className="admin-loading"><i className="fas fa-spinner fa-spin"></i><p>Loading...</p></div>
                ) : users.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '40px' }}>No VIP users found</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>VIP Status</th>
                                    <th>Expires At</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id}>
                                        <td>{user.email}</td>
                                        <td>
                                            {user.vip_status === 'vip_permanent' ? (
                                                <span className="status-badge status-vip">💎 Permanent</span>
                                            ) : user.vip_status === 'vip_temp' && user.vip_expires_at && new Date(user.vip_expires_at) > new Date() ? (
                                                <span className="status-badge status-vip">⏰ Temporary</span>
                                            ) : (
                                                <span className="status-badge status-regular">👤 Regular</span>
                                            )}
                                        </td>
                                        <td>{user.vip_expires_at ? formatDate(user.vip_expires_at) : 'Never'}</td>
                                        <td>{formatDate(user.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {/* Transactions */}
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '30px' }}>
                <h3 style={{ color: '#d4af37', marginBottom: '20px' }}>
                    <i className="fas fa-receipt"></i> VIP Transactions
                </h3>
                
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        className="admin-input"
                        placeholder="Search transactions..."
                        value={transactionSearch}
                        onChange={(e) => setTransactionSearch(e.target.value)}
                        style={{ flex: 1, minWidth: '250px' }}
                    />
                    <select 
                        className="admin-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="completed">✅ Completed</option>
                        <option value="pending">⏳ Pending</option>
                        <option value="failed">❌ Failed</option>
                    </select>
                    <button className="admin-btn admin-btn-secondary" onClick={() => mutateTransactions()}>
                        <i className="fas fa-sync-alt"></i> Refresh
                    </button>
                </div>
                
                {transactionsLoading ? (
                    <div className="admin-loading"><i className="fas fa-spinner fa-spin"></i><p>Loading...</p></div>
                ) : transactions.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '40px' }}>No transactions found</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Transaction ID</th>
                                    <th>User</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map(tx => (
                                    <tr key={tx.id}>
                                        <td><code>{tx.transaction_id || tx.id}</code></td>
                                        <td>{tx.user_accounts?.email || 'N/A'}</td>
                                        <td>Rp {tx.amount?.toLocaleString()}</td>
                                        <td>
                                            {tx.payment_status === 'completed' ? (
                                                <span className="status-badge status-online">Completed</span>
                                            ) : tx.payment_status === 'pending' ? (
                                                <span className="status-badge" style={{ background: 'rgba(255,184,0,0.15)', color: '#ffb800' }}>Pending</span>
                                            ) : (
                                                <span className="status-badge status-banned">Failed</span>
                                            )}
                                        </td>
                                        <td>{formatDate(tx.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default VIPManager;

